import { markLocale, reorderDateParts, splitDateOrder } from "./dateReorder.js";
import type { DateOrder } from "./dateReorder.js";
import { DAY_MS } from "./freshness.js";

// THE date formatting of the app (#390). One place decides which locale a date
// is written in, one place builds and caches the Intl formatters, and every
// date the athlete reads goes through here.
//
// Why a locale of its own: the UI language says how words are written, the
// date format says where the day and the month go. An Italian reading the app
// in English wants "03 Oct", not "Oct 03". `effectiveDateLocale` is the only
// function that combines the two; callers get a locale string and pass it to
// the formatters below, exactly as they used to pass the language.
//
// Pure and RN-portable: no storage, no React. The preference itself lives per
// device (next to the language), see the DateFormatProvider in @heraion/ui.

export const DATE_FORMAT_PREFS = ["auto", "dmy", "mdy", "ymd"] as const;
export type DateFormatPref = (typeof DATE_FORMAT_PREFS)[number];
export const DEFAULT_DATE_FORMAT: DateFormatPref = "auto";

export function normaliseDateFormat(raw: string | null | undefined): DateFormatPref {
  return (DATE_FORMAT_PREFS as readonly string[]).includes(raw ?? "")
    ? (raw as DateFormatPref)
    : DEFAULT_DATE_FORMAT;
}


// An English locale tag per order: English has a real locale for each, so its
// punctuation stays right. The other languages get their digits reordered.
const ENGLISH_BY_ORDER: Record<DateOrder, string> = {
  dmy: "en-GB",
  mdy: "en-US",
  ymd: "en-SE",
};

const orderCache = new Map<string, DateOrder | null>();

/** Which way a locale writes the date, read off Intl so no table can drift. */
export function dateOrderOf(locale: string): DateOrder | null {
  if (orderCache.has(locale)) return orderCache.get(locale) ?? null;
  let order: DateOrder | null = null;
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).formatToParts(new Date(Date.UTC(2026, 10, 22, 12)));
    const seq = parts
      .filter((p) => p.type === "day" || p.type === "month" || p.type === "year")
      .map((p) => p.type[0])
      .join("");
    order = seq === "dmy" || seq === "mdy" || seq === "ymd" ? seq : null;
  } catch {
    order = null;
  }
  orderCache.set(locale, order);
  return order;
}

/** Whether `locale` reads the clock in 12 hours, its -x-h12/h24 mark included;
 *  null when Intl cannot say. */
export function uses12h(locale: string): boolean | null {
  try {
    const raw = dateFormatter(locale, { hour: "numeric" }).resolvedOptions().hour12;
    return typeof raw === "boolean" ? raw : null;
  } catch {
    return null;
  }
}

const baseOf = (tag: string): string => tag.split(/[-_]/)[0]?.toLowerCase() ?? "";

// `auto`: the device's region. A device locale in the UI's own language (de-CH
// on a German UI) is used as is; an English UI borrows the device's order
// (day first on an Italian phone, month first on a US one).
function autoDateLocale(language: string, deviceLocale?: string | null): string {
  if (deviceLocale && baseOf(deviceLocale) === baseOf(language)) return deviceLocale;
  const order = deviceLocale ? dateOrderOf(deviceLocale) : null;
  if (!order || baseOf(language) !== "en") return language;
  return order === "ymd" ? markLocale(ENGLISH_BY_ORDER.ymd, "ymd", null) : ENGLISH_BY_ORDER[order];
}

/**
 * The locale dates are written in.
 * - `auto`: the device's region (autoDateLocale).
 * - `dmy` / `mdy` / `ymd`: that order for NUMERIC dates, whatever the device
 *   says. A month name keeps the language's order; the clock stays the one
 *   `auto` reads, so month first never switches a 24h reader to AM/PM.
 * `deviceLocale` is mobile's resolvedOptions().locale or web's
 * navigator.language; absent, `auto` is just the language.
 */
export function effectiveDateLocale(
  language: string,
  pref: DateFormatPref,
  deviceLocale?: string | null,
): string {
  const auto = autoDateLocale(language, deviceLocale);
  if (pref === "auto") return auto;
  const english = baseOf(language) === "en";
  const base = english ? ENGLISH_BY_ORDER[pref] : language;
  // en-GB and en-US write their digits in their order already.
  const order = english && pref !== "ymd" ? null : pref;
  const clock = uses12h(auto);
  return markLocale(base, order, clock !== null && clock !== uses12h(base) ? clock : null);
}

// ---------------------------------------------------------------------------
// Formatters. Building an Intl.DateTimeFormat loads ICU data and a chart or a
// list builds hundreds per render, so one is cached per (locale, options). A
// locale Intl cannot build falls back to English instead of throwing (Hermes),
// and the fallback is cached under the failed key: one failed build, not one
// per render.

// Day, month and year as digits: the shape the order preference governs.
const numericDate = (o: Intl.DateTimeFormatOptions): boolean =>
  o.dateStyle === "short" ||
  (o.dateStyle === undefined &&
    o.month !== "long" &&
    o.month !== "short" &&
    o.month !== "narrow" &&
    [o.day, o.month, o.year].filter((f) => f !== undefined).length >= 2);

const showsClock = (o: Intl.DateTimeFormatOptions): boolean =>
  (o.hour !== undefined || o.timeStyle !== undefined) &&
  o.hour12 === undefined &&
  o.hourCycle === undefined;

// A formatter whose format() puts day, month and year in `order`. An engine
// without formatToParts keeps the locale's own order rather than throwing.
function withOrder(base: Intl.DateTimeFormat, order: DateOrder): Intl.DateTimeFormat {
  if (typeof base.formatToParts !== "function") return base;
  const wrapped = Object.create(base) as Intl.DateTimeFormat;
  Object.defineProperties(wrapped, {
    format: { value: (d?: Date | number) => reorderDateParts(base.formatToParts(d), order) },
    formatToParts: { value: (d?: Date | number) => base.formatToParts(d) },
    resolvedOptions: { value: () => base.resolvedOptions() },
  });
  return wrapped;
}

function build(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat | null {
  try {
    return new Intl.DateTimeFormat(locale, opts);
  } catch {
    return null;
  }
}

// null: not even English could be built. Kept, so it is not retried either.
const dtfCache = new Map<string, Intl.DateTimeFormat | null>();

export function dateFormatter(
  locale: string,
  opts: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(opts)}`;
  let f = dtfCache.get(key);
  if (f === undefined) {
    const { locale: plain, order, hour12 } = splitDateOrder(locale);
    const o = hour12 !== null && showsClock(opts) ? { ...opts, hour12 } : opts;
    const base = build(plain, o) ?? (plain === "en" ? null : build("en", o));
    f = base && order && numericDate(o) ? withOrder(base, order) : base;
    dtfCache.set(key, f);
  }
  if (!f) throw new RangeError(`No date formatter for ${locale}`);
  return f;
}

const valid = (d: Date): boolean => !Number.isNaN(d.getTime());

// An Intl that cannot build even the English formatter (a stripped Hermes) must
// not take the screen down: the caller names what to print instead.
function attempt(format: () => string, fallback: () => string): string {
  try {
    return format();
  } catch {
    return fallback();
  }
}

/** "02/10/2026": numeric day, month and year in the locale's order. */
export function formatShortDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (!valid(d)) return "\u2014";
  return attempt(
    () => dateFormatter(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(d),
    () => d.toLocaleDateString(),
  );
}

/** "02 Sep 2026": the day with its month as a word and the year. */
export function formatDayMonthYear(d: Date, locale: string): string {
  if (!valid(d)) return "–";
  return attempt(
    () => dateFormatter(locale, { day: "2-digit", month: "short", year: "numeric" }).format(d),
    () => d.toLocaleDateString(),
  );
}

const YEAR_SHOWN_AFTER_DAYS = 300;

/** "18 Aug": the year only past 300 days, where the month alone could read as this year's. */
export function formatDayMonth(d: Date, locale: string, nowMs = Date.now()): string {
  if (!valid(d)) return "–";
  const withYear = nowMs - d.getTime() > YEAR_SHOWN_AFTER_DAYS * DAY_MS;
  return attempt(
    () =>
      dateFormatter(locale, {
        day: "numeric",
        month: "short",
        ...(withYear ? { year: "numeric" as const } : {}),
      }).format(d),
    () => d.toLocaleDateString(),
  );
}

/** "2 Oct, 14:35": readings that share a day (glucose). */
export function formatDayMonthTime(d: Date, locale: string): string {
  if (!valid(d)) return "–";
  return attempt(
    () =>
      dateFormatter(locale, {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d),
    () => d.toLocaleString(),
  );
}

/** "02 Oct 2026, 14:35": a full instant, for a sync time or a feedback stamp. */
export function formatDateTime(d: Date, locale: string): string {
  if (!valid(d)) return "–";
  return attempt(
    () =>
      dateFormatter(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d),
    () => d.toLocaleString(),
  );
}

/** "02 Oct": the axis label of the trend charts. */
export function formatAxisDay(d: Date, locale: string): string {
  if (!valid(d)) return "–";
  return attempt(
    () => dateFormatter(locale, { day: "2-digit", month: "short" }).format(d),
    () => d.toLocaleDateString(),
  );
}

export type WhenParts = { date?: boolean; time?: boolean };

/** The "when" of a moment: day and short month, clock time, or both. '' for an unparseable instant. */
export function formatWhen(at: Date | number | string, locale: string, parts: WhenParts): string {
  const d = at instanceof Date ? at : new Date(at);
  if (!valid(d)) return "";
  return attempt(
    () =>
      dateFormatter(locale, {
        ...(parts.date ? { day: "2-digit", month: "short" } : {}),
        ...(parts.time ? { hour: "2-digit", minute: "2-digit" } : {}),
      }).format(d),
    () => "",
  );
}
